import { GridGeometry } from "types/Photo";

/**
 * Reconstruct the grid overlay (yellow cell rectangles + per-cell "x.xxmm"
 * labels) on top of the raw inspection image, using ONLY the data we persist:
 *   - the raw image (from S3),
 *   - the n-by-m width grid (null = empty cell), and
 *   - the grid geometry (cell size + offsets, from gridGeometry).
 *
 * This mirrors the rectangles + labels drawn server-side in grid_detect.py.
 * The fine green/red per-scanline edge ticks drawn inside process_square are
 * intentionally NOT reproduced: they depend on per-column intermediate data
 * that is not stored, and are diagnostic rather than part of the grid readout.
 *
 * Returns a JPEG data URL of the reconstructed overlay.
 */
export async function reconstructOverlay(
    rawImageUrl: string,
    widths: (number | null)[][],
    geometry: GridGeometry,
    options: { jpegQuality?: number } = {}
): Promise<string> {
    const { jpegQuality = 0.9 } = options;
    const { dxy, xExtra, yExtra, rows, cols, imgWidth, imgHeight } = geometry;

    const canvas = document.createElement("canvas");
    canvas.width = imgWidth;
    canvas.height = imgHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not obtain a 2D canvas context");

    // Decode WITHOUT applying EXIF orientation, so the pixel space matches the
    // server. cv2.imdecode ignores EXIF, so the stored geometry is expressed in
    // that un-rotated space; a browser <img> would auto-rotate and shift the
    // coordinates. createImageBitmap({ imageOrientation: "none" }) avoids that.
    const source = await loadRawPixels(rawImageUrl);
    ctx.drawImage(source, 0, 0, imgWidth, imgHeight);

    // Yellow, matching cv2's (0,255,255) BGR (= RGB 255,255,0).
    ctx.strokeStyle = "rgb(255, 255, 0)";
    ctx.fillStyle = "rgb(255, 255, 0)";
    ctx.lineWidth = 3;

    // cv2 FONT_HERSHEY_SIMPLEX at fontScale 4 is ~90px tall and baseline
    // anchored. Canvas font metrics won't be pixel-identical to Hershey, but
    // the labels land in the same cells with the same values.
    ctx.font = "90px sans-serif";
    ctx.textBaseline = "alphabetic";

    for (let y = 0; y < rows; y++) {
        const sy = yExtra + y * dxy;
        for (let x = 0; x < cols; x++) {
            const sx = xExtra + x * dxy;

            // Cell rectangle — drawn for every cell, empty or not.
            ctx.strokeRect(sx, sy, dxy, dxy);

            // mm label — only for measured cells (null = empty). Same value,
            // format and position as grid_detect.py:143.
            const w = widths[y]?.[x];
            if (w != null) {
                const tx = sx + dxy / 2 - 180;
                const ty = sy + dxy / 2 + 45;
                ctx.fillText(`${w.toFixed(2)}mm`, tx, ty);
            }
        }
    }

    return canvas.toDataURL("image/jpeg", jpegQuality);
}

/**
 * Build the S3 URL for a stored raw image from its DynamoDB `filename` (the
 * s3_key), e.g. "apertures/<uuid>.jpg". Kept here so the reconstruction has a
 * single source of truth for where the raw image lives.
 */
export function rawImageUrlFromFilename(
    filename: string,
    bucket = "integrity-screen-ai-aperture-photos",
    region = "ap-southeast-2"
): string {
    return `https://${bucket}.s3.${region}.amazonaws.com/${filename}`;
}

/**
 * Fetch + decode the raw image without applying EXIF orientation. Fetching to a
 * (same-origin) blob first also avoids tainting the canvas, provided the S3
 * bucket returns permissive CORS headers for the GET.
 */
async function loadRawPixels(url: string): Promise<CanvasImageSource> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch raw image: ${response.status}`);
    const blob = await response.blob();

    if (typeof createImageBitmap === "function") {
        try {
            // imageOrientation: "none" -> do not apply EXIF (matches cv2).
            return await createImageBitmap(blob, { imageOrientation: "none" });
        } catch {
            // Fall through to the <img> path below.
        }
    }

    // Fallback for environments without createImageBitmap. NOTE: some browsers
    // apply EXIF orientation to <img>, which can rotate the coordinate space.
    const objectUrl = URL.createObjectURL(blob);
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve(img);
            URL.revokeObjectURL(objectUrl);
        };
        img.onerror = (e) => {
            URL.revokeObjectURL(objectUrl);
            reject(e);
        };
        img.src = objectUrl;
    });
}
